import { rem } from 'polished'
import React, { useState } from 'react'
import styled from 'styled-components'
import { type GridProps, type SpaceProps } from 'styled-system'
import { Image, IMAGE_LAYOUT } from 'components/Image'
import { Grid } from 'components/Layout'
import { ReadMoreText } from 'components/ReadMoreText'
import { H4, Text } from 'components/Typography'
import {
  COLOR_INTENT,
  FONT_SIZE,
  FONT_STACK,
  IMAGE_BASE_WIDTH,
  mq,
  RADIUS,
  SPACE,
} from 'Theme'
import { useMedia } from 'useMedia'

const IMAGE_WIDTH = { MOBILE: rem(56), TABLET: rem(76) }
const RECOMMENDATION_MAX_LENGTH = { MOBILE: 140, TABLET: 500 }

const Photo = styled(Image)`
  border-radius: ${RADIUS.CIRCLE};
`

const StyledReadMoreText = styled(ReadMoreText)`
  ${mq.to.TABLET`
      grid-column: 1/3;
  `}
`

const ReadMoreButton = styled(Text)`
  cursor: pointer;
`

interface ProductAskExpertProps extends GridProps, SpaceProps {
  copy?: string
  profileName?: string
  imageUrl?: string
  imageAlt?: string
}

export const ProductAskExpert: React.FC<React.PWC<ProductAskExpertProps>> = ({
  copy,
  profileName,
  imageUrl,
  imageAlt,
  ...rest
}) => {
  const { MOBILE: isMobile } = useMedia()
  const [isCopyExpanded, setIsCopyExpanded] = useState(false)

  if (!profileName || !copy || !imageUrl) {
    return null
  }
  return (
    <Grid
      gridTemplateColumns={{
        MOBILE: `${IMAGE_WIDTH.MOBILE} 1fr`,
        TABLET: `${IMAGE_WIDTH.TABLET} 1fr`,
      }}
      gridGap={{ MOBILE: SPACE.PX_15, TABLET: SPACE.PX_20 }}
      alignItems={{ MOBILE: 'center', TABLET: 'start' }}
      {...rest}
      my={SPACE.PX_22}
    >
      <Grid gridRow={{ MOBILE: '1/2', TABLET: '1/3' }}>
        <Photo
          baseWidth={IMAGE_BASE_WIDTH.PX_320}
          layout={IMAGE_LAYOUT.RESPONSIVE}
          alt={imageAlt || profileName}
          src={imageUrl}
          sizes={`(max-width: 768px) ${IMAGE_WIDTH.MOBILE}, ${IMAGE_WIDTH.TABLET}`}
        />
      </Grid>
      <H4
        mb="0"
        fontFamily={FONT_STACK.SERIF}
        fontSize={{ MOBILE: FONT_SIZE.PX_25, TABLET: FONT_SIZE.PX_24 }}
      >
        {`${profileName} recommends:`}
      </H4>
      <StyledReadMoreText
        text={`&lsquo;${copy}&rsquo;`}
        fontSize={FONT_SIZE.PX_14}
        maxAllowedLength={
          isMobile
            ? RECOMMENDATION_MAX_LENGTH.MOBILE
            : RECOMMENDATION_MAX_LENGTH.TABLET
        }
        MarkdownRendererProps={{
          fontFamily: FONT_STACK.SANS,
        }}
        isExpanded={isCopyExpanded}
        actionButton={() => (
          <ReadMoreButton
            color={COLOR_INTENT.BRAND}
            fontSize={FONT_SIZE.PX_14}
            onClick={() => setIsCopyExpanded((prevState) => !prevState)}
          >
            {isCopyExpanded ? 'Show less' : 'Read More'}
          </ReadMoreButton>
        )}
      />
    </Grid>
  )
}
